// socket/SocketManager.ts

import {ACCESS_KEY, ActionType, AdminRole, ChatMessageStatus, ConsultationStatus, PatientAccountTicketRequestStatus, ResourceType, UserRole} from '@/constants';
import {ChatMessage, HospitalConsultationCase, SocketEvent, SpecialistConsultation, SpecialistOnboardingItem} from '@/interface';
import {
  addChatMessage,
  updateConsultationData,
  updateAccountExportStatus,
  updateAccountDeletionStatus,
  resetAuth,
  resetPatientDashboardState,
  addHospitalConsultationCaseData,
  cancelCancelConsultationRequest,
  updateSpecialistConsultationData,
  addSpecialistConsultationData,
  specialistDashboardStatsRequest,
  updateHospitalConsultationCaseData,
  updateSpecialistConsulationSummaryData,
  updateSpecialistProfileStatus,
  updateSpecialistOnboardingProfileStatus,
  addFinancePaymentData,
  updateFinancePaymentStatus,
  addFinanceConsultationCaseData,
  addSupportTicketData,
  addNotification,
  activateOrDeactivateSpecialist,
  activateOrDeactivateHospitalSpecialist,
  logoutRequest,
  updateSpecialistInvitationStatus,
  updateFavouriteStatus,
  updateSpecialistData,
} from '../redux/slices';
import {store} from '../redux/store';
import {authDataSelector} from '../redux/selectors';
import {updateMessageEvent} from '.';
import {ToastType} from '@/ui-kits/Toast';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';

export class SocketManager {
  private socket: WebSocket | null = null;
  private readonly dispatch: any;
  private readonly onStatusChange: (open: boolean) => void;
  private readonly showGlobalToast: (message: string, type: ToastType) => void;

  constructor(dispatch: any, showGlobalToast: (message: string, type: ToastType) => void, onStatusChange: (open: boolean) => void) {
    this.dispatch = dispatch;
    this.onStatusChange = onStatusChange;
    this.showGlobalToast = showGlobalToast;
  }

  hasSocket(): boolean {
    return this.socket !== null;
  }

  connect(url: string) {
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      this.onStatusChange(true);
    };

    this.socket.onclose = () => {
      this.onStatusChange(false);
      this.socket = null;
    };

    this.socket.onmessage = event => this.handleSocketEvent(JSON.parse(event.data));
  }

  send(data: SocketEvent) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  close() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  // Add this method to SocketManager class
  isConnecting(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.CONNECTING;
  }

  isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  private getUserRole() {
    const data = authDataSelector(store.getState());
    return data?.role;
  }

  private getUserScope() {
    const data = authDataSelector(store.getState());
    return data?.scope;
  }

  private getUserId() {
    const data = authDataSelector(store.getState());
    return data?.id;
  }

  private getAccessToken() {
    return localStorage.getItem(ACCESS_KEY) || '';
  }

  private handleSocketEvent(event: SocketEvent) {
    if (event.resource_type === ResourceType.Message) {
      this.handleMessage(event);
    }
    if ([ResourceType.ConsultationCase, ResourceType.ConsultationSummary].includes(event.resource_type)) {
      this.handleConsultation(event);
    }
    if (event.resource_type === ResourceType.User) {
      this.handleUser(event);
    }
    if (event.resource_type === ResourceType.Feedback) {
      this.handleFeedback(event);
    }
    if (event.resource_type === ResourceType.Payment) {
      this.handlePayment(event);
    }
    if (event.resource_type === ResourceType.UserQueryTicket) {
      this.handleSupportTicket(event);
    }

    if (event.resource_type === ResourceType.Notification) {
      this.handleNotification(event);
    }

    if (event.resource_type === ResourceType.SpecialistInvitation) {
      this.handleSpecialistInvitation(event);
    }

    if (event.resource_type === ResourceType.Favourite) {
      this.handleFavourite(event);
    }

    if (event.resource_type === ResourceType.Specialist) {
      this.handleSpecialist(event);
    }

    if (event.resource_type === ResourceType.Hospital) {
      this.handleHospital(event);
    }

    // handle global toast
    if (event?.status && event?.status_code) {
      if (event.status === 'success') {
        this.showGlobalToast(getSuccessMessage(event.status_code as SuccessCodes), 'success');
      } else {
        this.showGlobalToast(getErrorMessage(event.status_code as ErrorCodes), 'failure');
      }
    }
  }

  private handleHospital(event: SocketEvent) {
    if (event.action_id === ActionType.Activated || event.action_id === ActionType.Deactivated) {
      if (this.getUserRole() === UserRole.PATIENT && event.resource_id !== this.getUserId()) {
        const is_active = event.action_id === ActionType.Activated;
        this.dispatch(activateOrDeactivateHospitalSpecialist({hospital_id: event.resource_id, is_active}));
      }
    }
  }

  private handleMessage(event: SocketEvent) {
    // ================================
    // Receive incomming message
    // ================================
    if (event.action_id === ActionType.Created) {
      // message payload
      const message: ChatMessage = {
        id: event.resource_id,
        ...event.data?.message,
      };

      const case_id: number = event.data?.consultation_id;
      const payload = {
        consultation_case_id: case_id,
        message: message,
      };

      // add the recieved message with delivered status
      this.dispatch(addChatMessage(payload));

      // send acknowledgement
      this.send(updateMessageEvent(event.resource_id, {status: ChatMessageStatus.Delivered}));
    }
  }

  private handleConsultation(event: SocketEvent) {
    const current_user_role = this.getUserRole();
    // ================================
    // update consultation data
    // ================================
    if (event.action_id === ActionType.Updated) {
      const update_payload: any = {};

      // chat active payload
      if (event.data?.chat_active) {
        update_payload.chat_active = event.data.chat_active;
      }
      
      if (event.data?.meet_link !== undefined) {
        update_payload.meet_link = event.data.meet_link;
      }
      if (event.data?.meet_time !== undefined) {
        update_payload.meet_time = event.data.meet_time;
      }

      // add status also
      if (event.data?.status) {
        update_payload.status = event.data.status;
      }

      if (event.data?.specialist) {
        update_payload.specialist = event.data.specialist;
      }

      if (event.data?.files) {
        update_payload.files = event.data.files;
      }

      if (event.data?.ai_summary) {
        update_payload.ai_summary = event.data.ai_summary;
      }
      if (event.data?.summary) {
        update_payload.summary = event.data.summary;
      }

      // dispatch slice code according to role
      if (current_user_role === UserRole.PATIENT) {
        this.dispatch(updateConsultationData({id: event.resource_id, data: update_payload}));

        // if status is under review by doctor
        if (update_payload.status?.id === ConsultationStatus.UnderReviewByDoctor) {
          this.dispatch(cancelCancelConsultationRequest({id: event.resource_id}));
          // this.dispatch(cancelUploadAdditionalFilesRequest({id: event.resource_id}));
        }
      }

      if (current_user_role === UserRole.SPECIALIST) {
        if (event.resource_type === ResourceType.ConsultationCase) {
          this.dispatch(updateSpecialistConsultationData({id: event.resource_id, data: update_payload}));
        } else {
          this.dispatch(updateSpecialistConsulationSummaryData({id: event.resource_id, data: update_payload}));
        }
      }
      if (current_user_role === UserRole.HOSPITAL_POC) {
        this.dispatch(updateHospitalConsultationCaseData({id: event.resource_id, data: update_payload}));
      }
    }

    // ================================
    // create consultation case
    // ================================
    if (event.action_id === ActionType.Created) {
      if (current_user_role === UserRole.SPECIALIST) {
        const consultation: SpecialistConsultation = {
          ...event.data,
        };

        this.dispatch(addSpecialistConsultationData(consultation));
      }
      if (current_user_role === UserRole.HOSPITAL_POC) {
        const consultation: HospitalConsultationCase = {
          ...event.data,
        };
        this.dispatch(addHospitalConsultationCaseData(consultation));
      }
      if (current_user_role === UserRole.ADMIN && this.getUserScope() === AdminRole.FinanceAdmin) {
        const consultation: HospitalConsultationCase = {
          ...event.data,
        };
        this.dispatch(addFinanceConsultationCaseData(consultation));
      }
    }
  }

  private handleFeedback(event: SocketEvent) {
    if (event.action_id === ActionType.Created) {
      if (this.getUserRole() === UserRole.SPECIALIST) {
        this.dispatch(specialistDashboardStatsRequest());
      }
    }
  }

  private handlePayment(event: SocketEvent) {
    if (event.action_id === ActionType.Created) {
      if (this.getUserRole() === UserRole.ADMIN && this.getUserScope() === AdminRole.FinanceAdmin) {
        const data = event.data || {};
        const payment = {
          transaction_id: data.transaction_id || '',
          case_id: data.case_id,
          patient: {
            id: data.patient?.id,
            name: data.patient?.name || '',
            email: '',
          },
          amount: data.amount ?? 0,
          status: data.status || 'Success',
          payment_date: data.payment_date || '',
        };
        this.dispatch(addFinancePaymentData(payment));
      }
    }
    if (event.action_id === ActionType.Updated) {
      if (this.getUserRole() === UserRole.ADMIN && this.getUserScope() === AdminRole.FinanceAdmin) {
        const case_id = event.data?.case_id ?? event.resource_id;
        const status = event.data?.status;
        if (case_id != null && status) {
          this.dispatch(updateFinancePaymentStatus({case_id: Number(case_id), status}));
        }
      }
    }
  }

  private handleSupportTicket(event: SocketEvent) {
    if (event.action_id === ActionType.Created) {
      if (this.getUserRole() === UserRole.ADMIN && this.getUserScope() === AdminRole.SupportAdmin) {
        this.dispatch(addSupportTicketData(event.data));
      }
    }
  }

  private handleUser(event: SocketEvent) {
    // ================================
    // update user data
    // ================================
    if (event.action_id === ActionType.Updated) {
      if (event.data?.account_export_status) {
        this.dispatch(updateAccountExportStatus(event.data.account_export_status));
      }
      if (event.data?.account_deletion_status) {
        // reset patient dashboard state if account deletion status is approved
        if (event.data.account_deletion_status === PatientAccountTicketRequestStatus.Approved) {
          this.dispatch(resetPatientDashboardState());
        }
        this.dispatch(updateAccountDeletionStatus(event.data.account_deletion_status));
      }
      if (event.data?.profile_status) {
        if (this.getUserRole() === UserRole.SPECIALIST) {
          this.dispatch(updateSpecialistProfileStatus(event.data.profile_status));
        }
        if (this.getUserRole() === UserRole.ADMIN && this.getUserScope() === AdminRole.SpecialistAdmin) {
          const data = event.data as Partial<SpecialistOnboardingItem>;
          this.dispatch(updateSpecialistOnboardingProfileStatus({id: event.resource_id, data}));
        }
      }
    }
    if (event.action_id === ActionType.Activated || event.action_id === ActionType.Deactivated) {
      // the affected user is not the current user means user is a patient and the affected user is a specialist
      if (this.getUserRole() === UserRole.PATIENT && event.resource_id !== this.getUserId()) {
        const is_active = Boolean(event.data?.is_active);
        this.dispatch(activateOrDeactivateSpecialist({id: event.resource_id, is_active}));
      }
      // user is the resource means user is deactivated means user must be logged out
      if (event.action_id === ActionType.Deactivated && event.resource_id === this.getUserId()) {
        this.dispatch(logoutRequest());
      }
    }
    // ================================
    // delete user data if user is deleted
    // ================================
    if (event.action_id === ActionType.Deleted) {
      if (event.resource_id === this.getUserId()) {
        this.dispatch(resetAuth());
      }
    }

    // ================================
    // logout user via real time
    // ================================
    if (event.action_id === ActionType.Logout) {
      if (event.resource_id === this.getUserId()) {
        // only logout this currect session
        const session_id = this.getAccessToken();
        const session_to_ignore = event.data?.ignore_session_id;
        if (session_to_ignore && session_to_ignore !== session_id) {
          this.dispatch(resetAuth());
        }
      }
    }
  }

  private handleSpecialist(event: SocketEvent) {
    // ================================
    // update specialist data for patient when specialist is updated
    // ================================
    const feeAmount = event.data?.charge ?? 0;
    const feeCurrency = event.data?.fee_currency ?? 0;

    if (event.action_id === ActionType.Updated) {
      this.dispatch(updateSpecialistData({id: event.resource_id, feeAmount, feeCurrency}));
    }
  }

  private handleNotification(event: SocketEvent) {
    if (event.action_id === ActionType.Created) {
      this.dispatch(addNotification({...event.data, id: event.resource_id}));
    }
  }

  private handleSpecialistInvitation(event: SocketEvent) {
    // ================================
    // Handle specialist signup event for Hospital POC
    // ================================
    if (event.action_id === ActionType.Signup) {
      if (this.getUserRole() === UserRole.HOSPITAL_POC) {
        const onboarding_status = event.data?.onboarding_status;
        if (onboarding_status) {
          this.dispatch(updateSpecialistInvitationStatus({
            id: event.resource_id,
            onboarding_status,
          }));
        }
      }
    }
  }

  private handleFavourite(event: SocketEvent) {
    // ================================
    // Handle favourite doctor add/remove events for Patient
    // ================================
    if (this.getUserRole() === UserRole.PATIENT) {
      const specialist_id = event.data?.specialist_id ?? event.resource_id;
      const is_favorite = event.data?.is_favorite;
      
      if (specialist_id != null && is_favorite !== undefined) {
        this.dispatch(updateFavouriteStatus({
          specialist_id: Number(specialist_id),
          is_favorite: Boolean(is_favorite),
        }));
      }
    }
  }
}
